import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { jsPDF } from "jspdf";
import { 
    BarChart3, 
    Calendar,
    AlertTriangle,
    ArrowRight,
    ArrowUp,
    ArrowDown,
    LogOut,
    Crown,
    Zap,
    Download,
    Eye,
    X,
    Settings,
    FileText,
    TrendingUp,
    User,
    CreditCard,
    Star,
    Globe,
    Link2,
    Search,
    GitCompare,
    Clock,
    Minus
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_design-scorecard/artifacts/dmpk6oxa_site%20grade%20pro%20logo%20a.png";

const TIER_CONFIG = {
    free: { name: "Free", color: "gray", badge: "bg-gray-500/20 text-gray-400" },
    starter: { name: "Starter", color: "emerald", badge: "bg-emerald-500/20 text-emerald-400" },
    pro: { name: "Pro", color: "blue", badge: "bg-blue-500/20 text-blue-400" },
    premium: { name: "Premium", color: "purple", badge: "bg-purple-500/20 text-purple-400" }
};

const TOOL_META = {
    site_crawl: { label: "Site Crawl", icon: Globe, color: "text-cyan-400", bg: "bg-cyan-500/10" },
    backlinks: { label: "Backlinks", icon: Link2, color: "text-orange-400", bg: "bg-orange-500/10" },
    keywords: { label: "Keywords", icon: Search, color: "text-emerald-400", bg: "bg-emerald-500/10" },
    competitor: { label: "Competitor", icon: GitCompare, color: "text-violet-400", bg: "bg-violet-500/10" },
};

/* ─── small sparkline drawn with inline SVG ─── */
function MiniTrend({ points }) {
    if (!points || points.length < 2) return null;
    const w = 80, h = 28, pad = 2;
    const scores = points.map(p => p.score);
    const min = Math.min(...scores) - 5;
    const max = Math.max(...scores) + 5;
    const range = max - min || 1;
    const coords = scores.map((s, i) => {
        const x = pad + (i / (scores.length - 1)) * (w - pad * 2);
        const y = h - pad - ((s - min) / range) * (h - pad * 2);
        return `${x},${y}`;
    });
    const trending = scores[scores.length - 1] >= scores[0];
    const stroke = trending ? "#10B981" : "#EF4444";
    return (
        <svg width={w} height={h} className="inline-block">
            <polyline fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" points={coords.join(" ")} />
        </svg>
    );
}

function UserDashboard({ onBack, onAnalyze }) {
    const { user, logout } = useAuth();
    const [analyses, setAnalyses] = useState([]);
    const [scoreTrends, setScoreTrends] = useState([]);
    const [toolHistory, setToolHistory] = useState([]);
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("scores");
    const [selectedAnalysis, setSelectedAnalysis] = useState(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [analysesRes, subRes, trendsRes, toolRes] = await Promise.all([
                axios.get(`${API}/user/analyses`, { withCredentials: true }),
                axios.get(`${API}/user/subscription`, { withCredentials: true }),
                axios.get(`${API}/user/score-trends`, { withCredentials: true }).catch(() => ({ data: { trends: [] } })),
                axios.get(`${API}/user/tool-history`, { withCredentials: true }).catch(() => ({ data: { history: [] } })),
            ]);
            setAnalyses(analysesRes.data.analyses || []);
            setSubscription(subRes.data);
            setScoreTrends(trendsRes.data.trends || []);
            setToolHistory(toolRes.data.history || []);
        } catch (error) {
            console.error("Failed to fetch user data:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleUpgrade = async (planId) => {
        try {
            const response = await axios.post(`${API}/checkout`, {
                plan_id: planId,
                origin_url: window.location.origin,
                email: user?.email
            }, { withCredentials: true });
            window.location.href = response.data.url;
        } catch (error) {
            console.error("Checkout failed:", error);
            toast.error("Failed to start checkout");
        }
    };

    const fmt = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "N/A";
    const fmtTime = (d) => d ? new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";
    const scoreColor = (s) => s >= 80 ? "text-emerald-400" : s >= 60 ? "text-amber-400" : "text-red-400";
    const scoreBg = (s) => s >= 80 ? "bg-emerald-500" : s >= 60 ? "bg-amber-500" : "bg-red-500";
    const badgeClass = (s) => s >= 80 ? "bg-emerald-500/20 text-emerald-400" : s >= 60 ? "bg-amber-500/20 text-amber-400" : "bg-red-500/20 text-red-400";

    const generatePDF = (analysis) => {
        if (!analysis) return;
        const doc = new jsPDF();
        const pw = doc.internal.pageSize.getWidth();
        doc.setFillColor(5, 5, 5);
        doc.rect(0, 0, pw, 45, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(22);
        doc.setFont("helvetica", "bold");
        doc.text("PRESSURE REPORT", 20, 25);
        doc.setFontSize(11);
        doc.setFont("helvetica", "normal");
        doc.text(`${analysis.url}`, 20, 38);
        let y = 60;
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.text("OVERALL SCORE", 20, y);
        const sc = analysis.overall_score >= 80 ? [16, 185, 129] : analysis.overall_score >= 60 ? [245, 158, 11] : [239, 68, 68];
        doc.setTextColor(...sc);
        doc.setFontSize(40);
        doc.text(`${analysis.overall_score}`, 20, y + 20);
        doc.setTextColor(100, 100, 100);
        doc.setFontSize(12);
        doc.text(`Grade: ${analysis.overall_grade}`, 60, y + 15);
        y += 35;
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(11);
        doc.setFont("helvetica", "normal");
        const sl = doc.splitTextToSize(analysis.summary || "", pw - 40);
        doc.text(sl, 20, y);
        y += sl.length * 6 + 10;
        (analysis.categories || []).forEach((cat) => {
            if (y > 260) { doc.addPage(); y = 20; }
            doc.setFontSize(11);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(0, 0, 0);
            doc.text(cat.name, 20, y);
            const cc = cat.score >= 80 ? [16, 185, 129] : cat.score >= 60 ? [245, 158, 11] : [239, 68, 68];
            doc.setTextColor(...cc);
            doc.text(`${cat.score}/100`, pw - 40, y);
            y += 12;
        });
        doc.save(`report-${analysis.url?.replace(/[^a-z0-9]/gi, "-") || "analysis"}.pdf`);
        toast.success("Report downloaded!");
    };

    const tier = user?.subscription_tier || "free";
    const tierConfig = TIER_CONFIG[tier] || TIER_CONFIG.free;

    /* ─── unified timeline: merge audits + tool history ─── */
    const timeline = [
        ...analyses.map(a => ({ ...a, _kind: "audit" })),
        ...toolHistory.map(t => ({ ...t, _kind: "tool" })),
    ].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")).slice(0, 50);

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0a0a0a]" data-testid="user-dashboard">
            <div className="fixed inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px] pointer-events-none" />
            
            {/* Header */}
            <header className="sticky top-0 z-50 bg-[#0a0a0a]/90 backdrop-blur-sm border-b border-white/5">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <img src={LOGO_URL} alt="SiteGradePro" className="h-9 w-auto cursor-pointer" onClick={onBack} data-testid="dashboard-logo" />
                        <div className="hidden sm:block">
                            <p className="text-white font-semibold">Dashboard</p>
                            <p className="text-xs text-gray-500">{user?.email}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button onClick={onAnalyze} size="sm" className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="new-analysis-btn">
                            <Zap className="h-4 w-4 mr-1.5" />New Scan
                        </Button>
                        <Button variant="ghost" size="sm" onClick={logout} className="text-gray-400 hover:text-white" data-testid="logout-btn">
                            <LogOut className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </header>

            <main className="relative max-w-6xl mx-auto px-4 sm:px-6 py-8">
                {/* Welcome + Plan */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                    <Card className="lg:col-span-2 bg-[#111111] border-white/10">
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between">
                                <div>
                                    <h1 className="text-2xl font-bold text-white mb-1" data-testid="dashboard-welcome">
                                        Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
                                    </h1>
                                    <p className="text-gray-500 text-sm">Manage your website scores and reports</p>
                                </div>
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${tierConfig.badge}`} data-testid="user-tier-badge">
                                    {tierConfig.name}
                                </span>
                            </div>
                            <div className="grid grid-cols-3 gap-4 mt-6">
                                <div className="text-center p-4 bg-white/5 rounded-lg" data-testid="stat-total-scans">
                                    <p className="text-2xl font-bold text-white">{analyses.length}</p>
                                    <p className="text-xs text-gray-500">Total Scans</p>
                                </div>
                                <div className="text-center p-4 bg-white/5 rounded-lg" data-testid="stat-scans-left">
                                    <p className="text-2xl font-bold text-violet-400">{subscription?.scans_remaining || 0}</p>
                                    <p className="text-xs text-gray-500">Scans Left</p>
                                </div>
                                <div className="text-center p-4 bg-white/5 rounded-lg" data-testid="stat-avg-score">
                                    <p className="text-2xl font-bold text-emerald-400">
                                        {analyses.length > 0 ? Math.round(analyses.reduce((a, b) => a + b.overall_score, 0) / analyses.length) : "-"}
                                    </p>
                                    <p className="text-xs text-gray-500">Avg Score</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-violet-600/20 to-purple-600/20 border-violet-500/30">
                        <CardContent className="p-6">
                            <div className="flex items-center gap-2 mb-3">
                                <Crown className="h-5 w-5 text-violet-400" />
                                <span className="text-white font-semibold">Upgrade Plan</span>
                            </div>
                            <p className="text-gray-400 text-sm mb-4">
                                {tier === "free" ? "Unlock unlimited scans & reports" : "Get more features & exports"}
                            </p>
                            <Button onClick={() => handleUpgrade(tier === "free" ? "pro_monthly" : "agency_monthly")} className="w-full bg-violet-600 hover:bg-violet-700 text-white" data-testid="upgrade-plan-btn">
                                {tier === "free" ? "Upgrade to Pro" : "Go Agency"}
                                <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>
                            <p className="text-xs text-gray-500 text-center mt-3">{tier === "free" ? "Starting at $19/mo" : "White-label reports"}</p>
                        </CardContent>
                    </Card>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 p-1 bg-white/5 rounded-lg mb-6 overflow-x-auto" data-testid="dashboard-tabs">
                    {[
                        { id: "scores", label: "Scores", icon: Star },
                        { id: "history", label: "History", icon: Clock },
                        { id: "trends", label: "Trends", icon: TrendingUp },
                        { id: "reports", label: "Reports", icon: FileText },
                        { id: "settings", label: "Settings", icon: Settings },
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                                activeTab === tab.id ? "bg-violet-600 text-white" : "text-gray-400 hover:text-white hover:bg-white/5"
                            }`}
                            data-testid={`tab-${tab.id}`}
                        >
                            <tab.icon className="h-4 w-4" />
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* ─── SCORES TAB ─── */}
                {activeTab === "scores" && (
                    <div className="space-y-4" data-testid="saved-scores-section">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-white">Saved Scores</h2>
                            <span className="text-sm text-gray-500">{analyses.length} analyses</span>
                        </div>
                        {analyses.length === 0 ? (
                            <Card className="bg-[#111111] border-white/10">
                                <CardContent className="p-12 text-center">
                                    <TrendingUp className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                                    <h3 className="text-white font-semibold mb-2">No scores yet</h3>
                                    <p className="text-gray-500 text-sm mb-6">Run your first website analysis</p>
                                    <Button onClick={onAnalyze} className="bg-violet-600 hover:bg-violet-700" data-testid="empty-analyze-btn">
                                        <Zap className="h-4 w-4 mr-2" />Analyze Website
                                    </Button>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="grid gap-3">
                                {analyses.map((analysis, i) => (
                                    <Card key={i} className="bg-[#111111] border-white/10 hover:border-white/20 transition-colors cursor-pointer" onClick={() => setSelectedAnalysis(analysis)} data-testid={`score-card-${i}`}>
                                        <CardContent className="p-4">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-4">
                                                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${scoreBg(analysis.overall_score)}`}>
                                                        <span className="text-xl font-bold text-white">{analysis.overall_score}</span>
                                                    </div>
                                                    <div>
                                                        <p className="text-white font-medium truncate max-w-[200px] sm:max-w-[300px]">{analysis.url?.replace(/^https?:\/\//, "")}</p>
                                                        <div className="flex items-center gap-2 text-xs text-gray-500">
                                                            <Calendar className="h-3 w-3" />{fmt(analysis.created_at)}
                                                            <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${badgeClass(analysis.overall_score)}`}>{analysis.overall_grade}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Button size="sm" variant="ghost" className="text-gray-400 hover:text-white" onClick={(e) => { e.stopPropagation(); setSelectedAnalysis(analysis); }}>
                                                        <Eye className="h-4 w-4" />
                                                    </Button>
                                                    <Button size="sm" variant="ghost" className="text-gray-400 hover:text-violet-400" onClick={(e) => { e.stopPropagation(); generatePDF(analysis); }} data-testid={`download-report-${i}`}>
                                                        <Download className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* ─── HISTORY TAB (unified timeline) ─── */}
                {activeTab === "history" && (
                    <div className="space-y-4" data-testid="history-section">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-white">Activity History</h2>
                            <span className="text-sm text-gray-500">{timeline.length} activities</span>
                        </div>
                        {timeline.length === 0 ? (
                            <Card className="bg-[#111111] border-white/10">
                                <CardContent className="p-12 text-center">
                                    <Clock className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                                    <h3 className="text-white font-semibold mb-2">No activity yet</h3>
                                    <p className="text-gray-500 text-sm mb-6">Run an audit or SEO tool to see history</p>
                                    <Button onClick={onAnalyze} className="bg-violet-600 hover:bg-violet-700" data-testid="empty-history-analyze-btn">
                                        <Zap className="h-4 w-4 mr-2" />Get Started
                                    </Button>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="grid gap-3">
                                {timeline.map((item, i) => {
                                    if (item._kind === "audit") {
                                        return (
                                            <Card key={`a-${i}`} className="bg-[#111111] border-white/10 hover:border-white/20 transition-colors cursor-pointer" onClick={() => setSelectedAnalysis(item)} data-testid={`history-audit-${i}`}>
                                                <CardContent className="p-4">
                                                    <div className="flex items-center gap-4">
                                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${scoreBg(item.overall_score)}`}>
                                                            <span className="text-lg font-bold text-white">{item.overall_score}</span>
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-white font-medium truncate">Website Audit</p>
                                                            <p className="text-xs text-gray-500 truncate">{item.url?.replace(/^https?:\/\//, "")}</p>
                                                        </div>
                                                        <div className="text-right shrink-0">
                                                            <span className={`px-2 py-0.5 rounded text-xs font-bold ${badgeClass(item.overall_score)}`}>{item.overall_grade}</span>
                                                            <p className="text-xs text-gray-500 mt-1">{fmtTime(item.created_at)}</p>
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        );
                                    }
                                    const meta = TOOL_META[item.tool] || TOOL_META.site_crawl;
                                    const Icon = meta.icon;
                                    const toolScore = item.score ?? item.domain_authority_estimate ?? item.estimated_organic_keywords ?? null;
                                    return (
                                        <Card key={`t-${i}`} className="bg-[#111111] border-white/10" data-testid={`history-tool-${i}`}>
                                            <CardContent className="p-4">
                                                <div className="flex items-center gap-4">
                                                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${meta.bg}`}>
                                                        <Icon className={`h-5 w-5 ${meta.color}`} />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-white font-medium">{meta.label}</p>
                                                        <p className="text-xs text-gray-500 truncate">{item.url?.replace(/^https?:\/\//, "") || "—"}</p>
                                                    </div>
                                                    <div className="text-right shrink-0">
                                                        {toolScore != null && (
                                                            <span className={`text-sm font-bold ${meta.color}`}>{typeof toolScore === "number" ? toolScore.toLocaleString() : toolScore}</span>
                                                        )}
                                                        <p className="text-xs text-gray-500 mt-1">{fmtTime(item.created_at)}</p>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* ─── TRENDS TAB ─── */}
                {activeTab === "trends" && (
                    <div className="space-y-4" data-testid="trends-section">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-white">Score Trends</h2>
                            <span className="text-sm text-gray-500">{scoreTrends.length} tracked sites</span>
                        </div>
                        {scoreTrends.length === 0 ? (
                            <Card className="bg-[#111111] border-white/10">
                                <CardContent className="p-12 text-center">
                                    <BarChart3 className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                                    <h3 className="text-white font-semibold mb-2">No trends yet</h3>
                                    <p className="text-gray-500 text-sm mb-6">
                                        {tier === "free" ? "Upgrade to Pro for score history" : "Run multiple audits on the same URL to see trends"}
                                    </p>
                                    {tier === "free" ? (
                                        <Button onClick={() => handleUpgrade("pro_monthly")} className="bg-violet-600 hover:bg-violet-700" data-testid="trends-upgrade-btn">
                                            <Crown className="h-4 w-4 mr-2" />Unlock Trends
                                        </Button>
                                    ) : (
                                        <Button onClick={onAnalyze} className="bg-violet-600 hover:bg-violet-700" data-testid="trends-analyze-btn">
                                            <Zap className="h-4 w-4 mr-2" />Run Audit
                                        </Button>
                                    )}
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="grid gap-4">
                                {scoreTrends.map((trend, i) => {
                                    const ChangeIcon = trend.change > 0 ? ArrowUp : trend.change < 0 ? ArrowDown : Minus;
                                    const changeColor = trend.change > 0 ? "text-emerald-400" : trend.change < 0 ? "text-red-400" : "text-gray-400";
                                    return (
                                        <Card key={i} className="bg-[#111111] border-white/10" data-testid={`trend-card-${i}`}>
                                            <CardContent className="p-5">
                                                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                                                    <div className="flex items-center gap-4 flex-1 min-w-0">
                                                        <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${scoreBg(trend.latest_score)}`}>
                                                            <span className="text-xl font-bold text-white">{trend.latest_score}</span>
                                                        </div>
                                                        <div className="min-w-0">
                                                            <p className="text-white font-medium truncate">{trend.url.replace(/^https?:\/\//, "")}</p>
                                                            <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                                                                <span>{trend.scans_count} scans</span>
                                                                <span>{fmt(trend.first_date)} — {fmt(trend.latest_date)}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        <MiniTrend points={trend.history} />
                                                        <div className="text-right">
                                                            <div className={`flex items-center gap-1 ${changeColor}`}>
                                                                <ChangeIcon className="h-4 w-4" />
                                                                <span className="font-bold">{Math.abs(trend.change)}</span>
                                                            </div>
                                                            <p className="text-xs text-gray-500">{trend.change_percent > 0 ? "+" : ""}{trend.change_percent}%</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* ─── REPORTS TAB ─── */}
                {activeTab === "reports" && (
                    <div className="space-y-4" data-testid="past-reports-section">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-white">Past Reports</h2>
                        </div>
                        {analyses.length === 0 ? (
                            <Card className="bg-[#111111] border-white/10">
                                <CardContent className="p-12 text-center">
                                    <FileText className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                                    <h3 className="text-white font-semibold mb-2">No reports yet</h3>
                                    <p className="text-gray-500 text-sm">Reports will appear after your first scan</p>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="grid gap-3">
                                {analyses.map((analysis, i) => (
                                    <Card key={i} className="bg-[#111111] border-white/10">
                                        <CardContent className="p-4">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center">
                                                        <FileText className="h-5 w-5 text-violet-400" />
                                                    </div>
                                                    <div>
                                                        <p className="text-white font-medium text-sm">
                                                            Pressure Report - {analysis.url?.replace(/^https?:\/\//, "").slice(0, 25)}
                                                        </p>
                                                        <p className="text-xs text-gray-500">{fmt(analysis.created_at)}</p>
                                                    </div>
                                                </div>
                                                <Button size="sm" onClick={() => generatePDF(analysis)} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid={`download-pdf-${i}`}>
                                                    <Download className="h-4 w-4 mr-1.5" />Download
                                                </Button>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* ─── SETTINGS TAB ─── */}
                {activeTab === "settings" && (
                    <div className="space-y-6" data-testid="account-settings-section">
                        <h2 className="text-lg font-semibold text-white">Account Settings</h2>
                        <Card className="bg-[#111111] border-white/10">
                            <CardContent className="p-6">
                                <div className="flex items-center gap-2 mb-4">
                                    <User className="h-5 w-5 text-violet-400" />
                                    <h3 className="text-white font-semibold">Profile</h3>
                                </div>
                                <div className="space-y-4">
                                    <div><label className="text-xs text-gray-500 uppercase tracking-wide">Email</label><p className="text-white mt-1">{user?.email}</p></div>
                                    <div><label className="text-xs text-gray-500 uppercase tracking-wide">Name</label><p className="text-white mt-1">{user?.name || "Not set"}</p></div>
                                    <div><label className="text-xs text-gray-500 uppercase tracking-wide">Member Since</label><p className="text-white mt-1">{user?.created_at ? fmt(user.created_at) : "N/A"}</p></div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="bg-[#111111] border-white/10">
                            <CardContent className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2"><CreditCard className="h-5 w-5 text-violet-400" /><h3 className="text-white font-semibold">Subscription</h3></div>
                                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${tierConfig.badge}`}>{tierConfig.name}</span>
                                </div>
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between py-3 border-b border-white/5"><span className="text-gray-400">Scans per month</span><span className="text-white">{subscription?.scans_per_month || 3}</span></div>
                                    <div className="flex items-center justify-between py-3 border-b border-white/5"><span className="text-gray-400">Scans remaining</span><span className="text-white">{subscription?.scans_remaining || 0}</span></div>
                                    <div className="flex items-center justify-between py-3"><span className="text-gray-400">History access</span><span className="text-white">{subscription?.history_days || 0} days</span></div>
                                </div>
                                {tier !== "premium" && (
                                    <Button onClick={() => handleUpgrade(tier === "free" ? "pro_monthly" : "agency_monthly")} className="w-full mt-6 bg-violet-600 hover:bg-violet-700 text-white" data-testid="settings-upgrade-btn">
                                        <Crown className="h-4 w-4 mr-2" />Upgrade Plan
                                    </Button>
                                )}
                            </CardContent>
                        </Card>
                        <Card className="bg-[#111111] border-red-500/20">
                            <CardContent className="p-6">
                                <h3 className="text-red-400 font-semibold mb-4">Danger Zone</h3>
                                <p className="text-gray-500 text-sm mb-4">Once you log out, you&apos;ll need to sign in again to access your dashboard.</p>
                                <Button variant="outline" onClick={logout} className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="logout-settings-btn">
                                    <LogOut className="h-4 w-4 mr-2" />Log Out
                                </Button>
                            </CardContent>
                        </Card>
                    </div>
                )}
            </main>

            {/* Analysis Detail Modal */}
            {selectedAnalysis && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="analysis-detail-modal">
                    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setSelectedAnalysis(null)} />
                    <Card className="relative z-10 bg-[#111111] border-white/10 w-full max-w-2xl max-h-[85vh] overflow-y-auto">
                        <CardContent className="p-0">
                            <div className="sticky top-0 z-10 bg-[#111111] border-b border-white/10 p-4 flex items-center justify-between">
                                <div>
                                    <h2 className="text-lg font-bold text-white">Analysis Details</h2>
                                    <p className="text-xs text-gray-500">{selectedAnalysis.url}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button size="sm" onClick={() => generatePDF(selectedAnalysis)} className="bg-violet-600 hover:bg-violet-700 text-white">
                                        <Download className="h-4 w-4 mr-1.5" />PDF
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => setSelectedAnalysis(null)} className="text-gray-400 hover:text-white" data-testid="close-modal-btn">
                                        <X className="h-5 w-5" />
                                    </Button>
                                </div>
                            </div>
                            <div className="p-6 space-y-6">
                                <div className="flex items-center gap-6 p-6 bg-white/5 rounded-xl">
                                    <div className={`w-20 h-20 rounded-xl flex items-center justify-center ${scoreBg(selectedAnalysis.overall_score)}`}>
                                        <span className="text-3xl font-bold text-white">{selectedAnalysis.overall_score}</span>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <h3 className="text-xl font-bold text-white">Overall Score</h3>
                                            <span className={`px-2 py-0.5 rounded text-xs font-bold ${selectedAnalysis.overall_score >= 80 ? "bg-emerald-500 text-white" : selectedAnalysis.overall_score >= 60 ? "bg-amber-500 text-white" : "bg-red-500 text-white"}`}>
                                                {selectedAnalysis.overall_grade}
                                            </span>
                                        </div>
                                        <p className="text-gray-400 text-sm">{selectedAnalysis.summary}</p>
                                    </div>
                                </div>
                                {selectedAnalysis.pressure_points?.length > 0 && (
                                    <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                                        <h4 className="text-red-400 font-semibold mb-3 flex items-center gap-2"><AlertTriangle className="h-4 w-4" />Critical Issues</h4>
                                        <ul className="space-y-2">
                                            {selectedAnalysis.pressure_points.map((point, idx) => (
                                                <li key={idx} className="text-gray-300 text-sm flex items-start gap-2"><span className="text-red-400">•</span>{point}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                <div>
                                    <h4 className="text-white font-semibold mb-4">Breakdown</h4>
                                    <div className="space-y-3">
                                        {(selectedAnalysis.categories || []).map((cat, idx) => (
                                            <div key={idx} className="p-4 bg-white/5 rounded-lg">
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="text-white font-medium">{cat.name}</span>
                                                    <div className="flex items-center gap-2">
                                                        <span className={`text-lg font-bold ${scoreColor(cat.score)}`}>{cat.score}</span>
                                                        <span className={`px-1.5 py-0.5 rounded text-xs ${badgeClass(cat.score)}`}>{cat.grade}</span>
                                                    </div>
                                                </div>
                                                <div className="w-full bg-white/10 rounded-full h-1.5">
                                                    <div className={`h-1.5 rounded-full ${scoreBg(cat.score)}`} style={{ width: `${cat.score}%` }} />
                                                </div>
                                                {cat.issues?.length > 0 && (
                                                    <div className="mt-3 space-y-1">
                                                        {cat.issues.slice(0, 2).map((issue, j) => (
                                                            <p key={j} className="text-xs text-gray-500 flex items-start gap-1"><AlertTriangle className="h-3 w-3 text-red-400 mt-0.5" />{issue}</p>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
}

export default UserDashboard;
